import React, { useRef } from 'react';
import { fileToDataURL, cropAndResizeDataURL } from './utils/imageUtils';

export default function IAQSection({ iaqReadings, updIAQ }) {
  const tempUploadRef = useRef(null);
  const pmUploadRef   = useRef(null);
  const fhUploadRef   = useRef(null);
  const co2UploadRef  = useRef(null);

  const handleSingle = async (field, file) => {
    const raw   = await fileToDataURL(file);
    const thumb = await cropAndResizeDataURL(raw, 280);
    updIAQ({ ...iaqReadings, [field]: thumb });
  };
  const removeSingle = field => updIAQ({ ...iaqReadings, [field]: null });

  return (
    <div className="border p-4 mb-6 rounded-lg">
      <h2 className="text-xl font-semibold mb-4">Indoor Air Quality</h2>

      {/* Temp / Humidity / AQI Group */}
      <div className="mb-6">
        <label className="block mb-1 font-medium">Temperature (°C)</label>
        <input
          type="number"
          step="0.1"
          className="w-full border rounded px-2 py-1 mb-3"
          value={iaqReadings.temperature || ''}
          onChange={e => updIAQ({ ...iaqReadings, temperature: e.target.value })}
        />

        <label className="block mb-1 font-medium">Relative Humidity (%)</label>
        <input
          type="number"
          step="0.1"
          className="w-full border rounded px-2 py-1 mb-3"
          value={iaqReadings.relativeHumidity || ''}
          onChange={e => updIAQ({ ...iaqReadings, relativeHumidity: e.target.value })}
        />

        <label className="block mb-1 font-medium">AQI</label>
        <input
          type="number"
          className="w-full border rounded px-2 py-1 mb-3"
          value={iaqReadings.aqi || ''}
          onChange={e => updIAQ({ ...iaqReadings, aqi: e.target.value })}
        />

        {/* Photo for Temp/Humidity/AQI */}
        <label className="block mb-1 font-medium">Photo</label>
        <input
          ref={tempUploadRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={e =>
            e.target.files[0] &&
            handleSingle('temperatureHumidityPicture', e.target.files[0])
          }
        />
        <button
          className="bg-green-600 text-white px-4 py-2 rounded mb-3"
          onClick={() => tempUploadRef.current.click()}
        >
          Upload Photo
        </button>
        {iaqReadings.temperatureHumidityPicture && (
          <div className="flex items-center gap-4">
            <img
              src={iaqReadings.temperatureHumidityPicture}
              alt=""
              className="w-24 h-24 object-cover rounded"
            />
            <button
              className="bg-red-500 text-white px-2 py-1 rounded"
              onClick={() => removeSingle('temperatureHumidityPicture')}
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* PM Group */}
      <div className="mb-6">
        <label className="block mb-1 font-medium">PM₂.₅ (µg/m³)</label>
        <input
          type="number"
          step="0.1"
          className="w-full border rounded px-2 py-1 mb-3"
          value={iaqReadings.pm25 || ''}
          onChange={e => updIAQ({ ...iaqReadings, pm25: e.target.value })}
        />

        <label className="block mb-1 font-medium">PM₁₀ (µg/m³)</label>
        <input
          type="number"
          step="0.1"
          className="w-full border rounded px-2 py-1 mb-3"
          value={iaqReadings.pm10 || ''}
          onChange={e => updIAQ({ ...iaqReadings, pm10: e.target.value })}
        />

        <label className="block mb-1 font-medium">Particle Count (#/L)</label>
        <input
          type="number"
          className="w-full border rounded px-2 py-1 mb-3"
          value={iaqReadings.particleCount || ''}
          onChange={e => updIAQ({ ...iaqReadings, particleCount: e.target.value })}
        />

        {/* Photo for PM */}
        <label className="block mb-1 font-medium">Photo</label>
        <input
          ref={pmUploadRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={e =>
            e.target.files[0] && handleSingle('pmPicture', e.target.files[0])
          }
        />
        <button
          className="bg-green-600 text-white px-4 py-2 rounded mb-3"
          onClick={() => pmUploadRef.current.click()}
        >
          Upload Photo
        </button>
        {iaqReadings.pmPicture && (
          <div className="flex items-center gap-4">
            <img
              src={iaqReadings.pmPicture}
              alt=""
              className="w-24 h-24 object-cover rounded"
            />
            <button
              className="bg-red-500 text-white px-2 py-1 rounded"
              onClick={() => removeSingle('pmPicture')}
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Formaldehyde/TVOC Group */}
      <div className="mb-6">
        <label className="block mb-1 font-medium">Formaldehyde (ppm)</label>
        <input
          type="number"
          step="0.001"
          className="w-full border rounded px-2 py-1 mb-3"
          value={iaqReadings.formaldehyde || ''}
          onChange={e => updIAQ({ ...iaqReadings, formaldehyde: e.target.value })}
        />

        <label className="block mb-1 font-medium">TVOC (ppm)</label>
        <input
          type="number"
          step="0.001"
          className="w-full border rounded px-2 py-1 mb-3"
          value={iaqReadings.tvoc || ''}
          onChange={e => updIAQ({ ...iaqReadings, tvoc: e.target.value })}
        />

        {/* Photo for Formaldehyde/TVOC */}
        <label className="block mb-1 font-medium">Photo</label>
        <input
          ref={fhUploadRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={e =>
            e.target.files[0] &&
            handleSingle('formaldehydeTvocPicture', e.target.files[0])
          }
        />
        <button
          className="bg-green-600 text-white px-4 py-2 rounded mb-3"
          onClick={() => fhUploadRef.current.click()}
        >
          Upload Photo
        </button>
        {iaqReadings.formaldehydeTvocPicture && (
          <div className="flex items-center gap-4">
            <img
              src={iaqReadings.formaldehydeTvocPicture}
              alt=""
              className="w-24 h-24 object-cover rounded"
            />
            <button
              className="bg-red-500 text-white px-2 py-1 rounded"
              onClick={() => removeSingle('formaldehydeTvocPicture')}
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* CO₂ Group */}
      <div>
        <label className="block mb-1 font-medium">CO₂ (ppm)</label>
        <input
          type="number"
          className="w-full border rounded px-2 py-1 mb-3"
          value={iaqReadings.co2 || ''}
          onChange={e => updIAQ({ ...iaqReadings, co2: e.target.value })}
        />

        {/* Photo for CO₂ */}
        <label className="block mb-1 font-medium">Photo</label>
        <input
          ref={co2UploadRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={e =>
            e.target.files[0] && handleSingle('co2AqiPicture', e.target.files[0])
          }
        />
        <button
          className="bg-green-600 text-white px-4 py-2 rounded mb-3"
          onClick={() => co2UploadRef.current.click()}
        >
          Upload Photo
        </button>
        {iaqReadings.co2AqiPicture && (
          <div className="flex items-center gap-4">
            <img
              src={iaqReadings.co2AqiPicture}
              alt=""
              className="w-24 h-24 object-cover rounded"
            />
            <button
              className="bg-red-500 text-white px-2 py-1 rounded"
              onClick={() => removeSingle('co2AqiPicture')}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
